import React from 'react';
import { Edit, Trash2, AlertCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';


export class GroupsTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      expandedRow: null
    };

    this.toggleRow = this.toggleRow.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleRowsPerPageChange = this.handleRowsPerPageChange.bind(this);
  }

  toggleRow(id) {
    this.setState(prevState => ({
      expandedRow: prevState.expandedRow === id ? null : id
    }));
  }

  handlePageChange(page) {
    if (this.props.onPageChange) {
      this.props.onPageChange(page);
    }
  }

  handleRowsPerPageChange(event) {
    if (this.props.onRowsPerPageChange) {
      this.props.onRowsPerPageChange(parseInt(event.target.value));
    }
  }

  render() {
    const {
      groups = [],
      onEdit,
      onDelete,
      isLoading = false,
      currentPage = 1,
      totalRows = 0,
      rowsPerPage = 15
    } = this.props;

    const totalPages = Math.ceil(totalRows / rowsPerPage);
    const startRow = (currentPage - 1) * rowsPerPage + 1;
    const endRow = Math.min(currentPage * rowsPerPage, totalRows);

    if (isLoading) {
      return (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading groups...</p>
        </div>
      );
    }

    if (!groups.length && !isLoading) {
      return (
        <div className="empty-state">
          <AlertCircle size={48} />
          <h3>No groups found</h3>
          <p>Create your first group to get started</p>
        </div>
      );
    }

    return (
      <div className="groups-table-container">
        <div className="table-header">

          <span
            style={{ fontSize: '1rem', marginRight: '0.5rem' }}
            onClick={() => {
              this.props.handleSidebar( !this.props.hideSidebar );
            }}
          >
            {this.props.hideSidebar && <i className="icon-size-actual"></i>}
            {!this.props.hideSidebar && <i className="icon-size-fullscreen"></i>}
          </span>

          <h2>All Groups</h2>
          <span className="count-badge">{totalRows} groups</span>
        </div>

        <div className="table-responsive">
          <table className="groups-table">
            <thead>
              <tr>
                <th>ID</th>
                <th></th>
                <th>Client</th>
                <th>Auctions</th>
                <th>Group Name</th>
                <th>Reg. Start Date</th>
                <th>Reg. End Date</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.id} className="table-row">
                  <td className="id-cell">{group.id}</td>
                  <td className="image-cell">
                    {group.image && (
                      <a href={group.image} target="_blank" rel="noopener noreferrer">
                        <img
                          src={group.image}
                          alt={group.group_name}
                          className="group-image"
                        />
                      </a>
                    )}
                  </td>
                  <td className="client-cell">
                    {group.organization_info?.organization_name || 'N/A'}
                  </td>
                  <td className="auctions-cell">
                    <span style={{ color: "black" }}>{group.total_auctons || 0}</span>
                  </td>
                  <td className="name-cell">
                    <div className="group-names">
                      <span className="name-en">{group.group_name}</span>
                      <span className="name-ar" >{group.group_name_ar}</span>
                    </div>
                  </td>
                  <td className="date-cell">{group.reg_start_date_formatted}</td>
                  <td className="date-cell">{group.reg_end_date_formatted}</td>
                  <td className="date-cell">{group.start_date_formatted}</td>
                  <td className="date-cell">{group.end_date_formatted}</td>
                  <td className="actions-cell">
                    <div className="action-buttons">
                      <button
                        className="action-btn edit-btn"
                        onClick={() => onEdit(group.id)}
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      {group.can_delete ? (
                        <button
                          className="action-btn delete-btn"
                          onClick={() => onDelete(group.id)}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      ) : (
                        <button
                          className="action-btn info-btn"
                          disabled
                          title="Linked to auction - Can't delete"
                        >
                          <AlertCircle size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Section */}
        <div className="pagination-container">
          <div className="pagination-info">
            <div className="rows-per-page">
              <label>Rows per page:</label>
              <select
                value={rowsPerPage}
                onChange={this.handleRowsPerPageChange}
                className="rows-select"
              >
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <span className="page-info">
              {startRow}-{endRow} of {totalRows}
            </span>
          </div>

          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => this.handlePageChange(1)}
              disabled={currentPage === 1}
              title="First page"
            >
              <ChevronsLeft size={18} />
            </button>
            <button
              className="pagination-btn"
              onClick={() => this.handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              title="Previous page"
            >
              <ChevronLeft size={18} />
            </button>

            <div className="page-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => this.handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              className="pagination-btn"
              onClick={() => this.handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              title="Next page"
            >
              <ChevronRight size={18} />
            </button>
            <button
              className="pagination-btn"
              onClick={() => this.handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              title="Last page"
            >
              <ChevronsRight size={18} />
            </button>
          </div>
        </div>
      </div>
    );
  }
}

